/**
 * Reports Client
 * Handles report generation with FormData and file downloads.
 * Follows Custom Method Pattern from client factory architecture (Pattern 3).
 * @module api/clients/reports
 */
import axios from "axios"
import api from "@/services/api"
import { XLSX_MEDIA_TYPE } from "@/api/mediaTypes"
import {
  applyEditorApiRequestHeaders,
  createTransportRequestId,
  normalizeRequestId,
  toSameOriginUrl,
} from "@/services/api"
import { downloadFile } from "@/helpers/Download"
import { normalizeReportsOrdersExclUsersResponse } from "@/api/normalizers"

// LLM:METADATA
// :hierarchy: [Domain:Reports | API_Client | Factory]
// :relates-to: uses: "api.js", uses: "Download.js", required-by: "useReports.js"
// :rationale: "Handles FormData-based report generation with blob responses and file downloads. Uses custom pattern instead of contractClient due to responseType: 'blob' requirement which createContractClient doesn't support."
// :contract: pre: "report name and params provided", post: "file downloaded or error thrown", invariant: "loading state cleared on completion (finally block)"
// :decision_cache: "Chose custom pattern (Pattern 3) instead of contractClient for FormData/blob handling complexity. Direct api.post allows responseType: 'blob' configuration which is required for file downloads. Contract validation not needed for binary responses. [REPORTS-CLIENT-001]"
// :references: SPEC: "Phase 3B Client Factory Implementation", ARCH: "client-factory-architecture.md Pattern 3"
// LLM:END

/**
 * Mapping of report names to backend format
 */
const REPORT_NAME_MAPPING = {
  sales: (pType) => `sales_${pType}`,
  bertus: () => "ext_cat",
}

const LINKED_REPORT_ACTIONS = {
  search_stat: {
    path: "/edit/newreports/search_stat",
    transport: "open",
  },
  artist_cards: {
    path: "/edit/newreports/artist_cards",
    transport: "download",
    downloadName: "artists",
    fallbackExtension: "xlsx",
  },
  check_check: {
    path: "/edit/newreports/check_check",
    transport: "open",
  },
  report_top_artists: {
    path: "/edit/newreports/report_top_artists",
    transport: "open",
  },
  report_photos: {
    path: "/edit/newreports/report_photos",
    transport: "download",
    downloadName: "photo_lp",
    fallbackExtension: "xlsx",
  },
  report_no_keys: {
    path: "/edit/newreports/report_no_keys",
    transport: "download",
    downloadName: "lp_no_keys",
    fallbackExtension: "xlsx",
  },
  report_double_1c: {
    path: "/edit/newreports/report_double_1c",
    transport: "download",
    downloadName: "photo_1c_copies",
    fallbackExtension: "xlsx",
  },
  add_offline_copies: {
    path: "/edit/newreports/add_offline_copies",
    transport: "download",
    downloadName: "add_copies",
    fallbackExtension: "xlsx",
  },
  fullcat_lp: {
    path: "/api/reports/fullcat/lp",
    transport: "download",
    downloadName: "catalog_lp_admin",
    fallbackExtension: "xlsx",
  },
  fullcat_cd: {
    path: "/api/reports/fullcat/cd",
    transport: "download",
    downloadName: "catalog_cd_admin",
    fallbackExtension: "xlsx",
  },
  logs: {
    path: "/edit/newreports/logs",
    transport: "open",
  },
}

let linkedReportTransport = null

const createLinkedReportTransport = () =>
  axios.create({
    withCredentials: true,
    timeout: 0,
  })

const getLinkedReportTransport = () => {
  if (!linkedReportTransport) {
    linkedReportTransport = createLinkedReportTransport()
  }

  return linkedReportTransport
}

export { XLSX_MEDIA_TYPE }

/**
 * Build the authenticated request config for one linked report action.
 * Download actions declare the workbook media type in Accept: Newreports
 * answers JSON to the default axios Accept list, which the XLSX validator
 * rejects, so the download contract must request the spreadsheet explicitly.
 *
 * @param {{transport: string}} actionConfig - Linked report action contract
 * @returns {Object} Axios request config with headers, blob response type and no timeout
 */
const createLinkedReportRequestConfig = (actionConfig) => {
  const config = {
    headers: {},
    responseType: "blob",
    timeout: 0,
  }

  applyEditorApiRequestHeaders(config)
  if (actionConfig.transport === "download") {
    config.headers.Accept = XLSX_MEDIA_TYPE
  }
  const requestId =
    normalizeRequestId(config.headers["X-Request-ID"]) ||
    createTransportRequestId()

  config.headers["X-Request-ID"] = requestId
  return config
}

const getResponseHeader = (response, headerName) => {
  const headers = response?.headers
  if (!headers) {
    return null
  }

  if (typeof headers.get === "function") {
    return headers.get(headerName)
  }

  return headers[headerName] ?? headers[headerName.toLowerCase()] ?? null
}

const readBlobBytes = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve(new Uint8Array(reader.result || new ArrayBuffer(0)))
    reader.onerror = () => reject(new Error("Failed to read XLSX response"))
    reader.onabort = () =>
      reject(new Error("Reading XLSX response was aborted"))
    reader.readAsArrayBuffer(blob)
  })

/**
 * Validate the binary response contract before a browser download sink.
 *
 * @param {Blob} blob - Resolved HTTP response payload
 * @param {string|null} responseContentType - Response Content-Type when supplied
 * @returns {Promise<Blob>} The original Blob after validation
 * @throws {Error} When the payload is empty, MIME-inconsistent, unreadable, or
 * lacks the XLSX ZIP local-file signature
 */
export const validateXlsxBlob = async (blob, responseContentType = null) => {
  if (typeof Blob !== "function" || !(blob instanceof Blob)) {
    throw new Error("XLSX response must be a Blob")
  }

  if (blob.size === 0) {
    throw new Error("XLSX response must not be empty")
  }

  const declaredContentType =
    (typeof responseContentType === "string" && responseContentType.trim()) ||
    blob.type ||
    ""
  const mediaType = declaredContentType.split(";", 1)[0].trim().toLowerCase()
  const allowedMediaTypes = new Set([
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/octet-stream",
  ])

  if (mediaType && !allowedMediaTypes.has(mediaType)) {
    throw new Error(`Unexpected XLSX response content type: ${mediaType}`)
  }

  let bytes
  try {
    bytes = await readBlobBytes(blob)
  } catch (error) {
    throw error instanceof Error && error.message
      ? error
      : new Error("Failed to read XLSX response")
  }

  if (
    bytes.length < 4 ||
    bytes[0] !== 0x50 ||
    bytes[1] !== 0x4b ||
    bytes[2] !== 0x03 ||
    bytes[3] !== 0x04
  ) {
    throw new Error("XLSX response is not a ZIP workbook")
  }

  return blob
}

const decodeHeaderFilename = (value) => {
  if (typeof value !== "string") {
    return null
  }

  try {
    return decodeURIComponent(value)
  } catch (_error) {
    return null
  }
}

const parseContentDispositionFilename = (contentDisposition) => {
  if (typeof contentDisposition !== "string" || !contentDisposition.trim()) {
    return null
  }

  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (utf8Match) {
    return decodeHeaderFilename(utf8Match[1].trim())
  }

  const genericExtendedMatch = contentDisposition.match(
    /filename\*\s*=\s*([^;]+)/i
  )
  if (genericExtendedMatch) {
    const rawValue = genericExtendedMatch[1].trim().replace(/^"|"$/g, "")
    const [, encodedValue = rawValue] = rawValue.split("''")
    return decodeHeaderFilename(encodedValue.trim())
  }

  const basicMatch = contentDisposition.match(
    /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i
  )
  if (!basicMatch) {
    return null
  }

  return (basicMatch[1] || basicMatch[2] || "").trim()
}

const sanitizeDownloadFilename = (filename) => {
  if (typeof filename !== "string") {
    return null
  }

  const trimmedFilename = filename.trim().replace(/[\u0000-\u001F\u007F]/g, "")
  if (!trimmedFilename) {
    return null
  }

  if (
    trimmedFilename.includes("/") ||
    trimmedFilename.includes("\\") ||
    trimmedFilename === "." ||
    trimmedFilename === ".."
  ) {
    return null
  }

  return trimmedFilename
}

const fallbackLinkedReportFilename = (actionConfig, actionName) =>
  `report_${actionConfig.downloadName || actionName}.${actionConfig.fallbackExtension || "xls"}`

export const resolveLinkedReportFilename = (
  response,
  actionConfig,
  actionName
) => {
  const headerFilename = sanitizeDownloadFilename(
    parseContentDispositionFilename(
      getResponseHeader(response, "content-disposition")
    )
  )

  return (
    headerFilename || fallbackLinkedReportFilename(actionConfig, actionName)
  )
}

export const downloadLinkedReportBlob = (blob, filename) => {
  const href = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = href
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(href)
}

const openReservedReportTab = () => window.open("", "_blank")

const escapeHtmlAttribute = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")

const injectHtmlBaseHref = (html, baseHref) => {
  if (typeof html !== "string" || !html.trim() || !baseHref) {
    return html
  }

  const safeBaseHref = escapeHtmlAttribute(baseHref)
  const baseTag = `<base href="${safeBaseHref}">`

  if (/<base\b[^>]*>/i.test(html)) {
    return html.replace(/<base\b[^>]*>/i, baseTag)
  }

  if (/<head\b[^>]*>/i.test(html)) {
    return html.replace(/<head\b[^>]*>/i, (match) => `${match}${baseTag}`)
  }

  if (/<html\b[^>]*>/i.test(html)) {
    return html.replace(
      /<html\b[^>]*>/i,
      (match) => `${match}<head>${baseTag}</head>`
    )
  }

  return `${baseTag}${html}`
}

const readBlobText = (blob) => {
  if (blob && typeof blob.text === "function") {
    return blob.text()
  }

  if (typeof Response === "function") {
    return new Response(blob).text()
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () =>
      reject(reader.error || new Error("Failed to read blob"))
    reader.readAsText(blob)
  })
}

/**
 * Decode a JSON error body that arrived as a Blob because the request asked
 * for `responseType: "blob"`. On success `error.response.data` becomes the
 * parsed object, so the central error handler can show the server `errors`.
 * Non-Blob, non-JSON or unparsable bodies leave the error untouched.
 *
 * @param {unknown} error - Rejected axios error
 * @returns {Promise<unknown>} The same error instance
 */
const decodeBlobErrorBody = async (error) => {
  const data = error?.response?.data
  if (typeof Blob !== "function" || !(data instanceof Blob)) {
    return error
  }
  const contentType = String(
    getResponseHeader(error.response, "content-type") || data.type || ""
  ).toLowerCase()
  if (!contentType.includes("application/json")) {
    return error
  }
  try {
    error.response.data = JSON.parse(await readBlobText(data))
  } catch (_parseError) {
    // Keep the original Blob: an unreadable body must not mask the HTTP error.
  }
  return error
}

const createHtmlPreviewBlob = async (blob, baseHref) => {
  const html = await readBlobText(blob)
  // The text is re-encoded as UTF-8, so the preview must declare that charset;
  // without it the browser guesses windows-1252 and garbles Cyrillic.
  const mediaType = (blob.type || "text/html").split(";")[0].trim()
  return new Blob([injectHtmlBaseHref(html, baseHref)], {
    type: `${mediaType};charset=utf-8`,
  })
}

const attachPreviewLoadCleanup = (previewTarget, previewUrl) => {
  let revoked = false
  const revoke = () => {
    if (revoked) return
    revoked = true
    window.URL.revokeObjectURL(previewUrl)
  }

  if (!previewTarget || typeof previewTarget.addEventListener !== "function") {
    // `noopener` may return no WindowProxy even though the browser created the
    // destination. Keep the Blob alive for a bounded interval instead of
    // invalidating that navigation synchronously.
    window.setTimeout(revoke, 60_000)
    return
  }

  previewTarget.addEventListener("load", revoke, { once: true })
}

const openBlobInNewTab = async (
  blob,
  reservedTab = null,
  { baseHref = null } = {}
) => {
  const previewBlob = baseHref
    ? await createHtmlPreviewBlob(blob, baseHref)
    : blob
  const previewUrl = window.URL.createObjectURL(previewBlob)
  let previewTarget = reservedTab
  if (reservedTab && !reservedTab.closed) {
    reservedTab.location.href = previewUrl
  } else {
    previewTarget = window.open(previewUrl, "_blank", "noopener")
  }
  attachPreviewLoadCleanup(previewTarget, previewUrl)
}

const closeReservedReportTab = (reservedTab) => {
  if (
    reservedTab &&
    !reservedTab.closed &&
    typeof reservedTab.close === "function"
  ) {
    reservedTab.close()
  }
}

/**
 * Create reports client with file download support
 *
 * @returns {Object} Reports client with generateReport and isPermitted methods
 *
 * @example
 * const reportsClient = createReportsClient()
 * await reportsClient.generateReport('sales', { pType: 'daily', date: '2026-02-20' })
 */
export function createReportsClient() {
  /**
   * Normalize report name according to backend expectations
   *
   * @param {string} name - Report name
   * @param {Object} reportParams - Report parameters containing pType for sales reports
   * @returns {string} Normalized report name
   */
  const normalizeReportName = (name, reportParams = {}) => {
    if (REPORT_NAME_MAPPING[name]) {
      return REPORT_NAME_MAPPING[name](reportParams.pType)
    }
    return name
  }

  /**
   * Build FormData payload for report request
   *
   * @param {string} req_name - Normalized report name
   * @param {Object} params - All report parameters (reportParams + moreParams)
   * @returns {FormData} Form data payload
   */
  const buildReportPayload = (req_name, params = {}) => {
    const payload = new FormData()
    payload.append(req_name, "1")

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      payload.append(key, String(value))
    })

    return payload
  }

  return {
    /**
     * Fetch and validate the users exclusion string for the orders report.
     *
     * @returns {Promise<{ users: string }>} Validated users exclusion payload
     */
    async getOrdersExclUsers() {
      const response = await api.get(
        toSameOriginUrl("/edit/newreports/orders_excl_users")
      )
      return normalizeReportsOrdersExclUsersResponse(response.data)
    },

    /**
     * Generate and download report
     *
     * Makes POST request with FormData to /edit/newreports endpoint.
     * Response is a blob (Excel file) that triggers browser download.
     *
     * @param {string} name - Report name (sales, favorites, bertus, etc.)
     * @param {Object} reportParams - Report parameters
     * @param {string} [reportParams.pType] - Report type (for sales reports: daily, weekly, etc.)
     * @param {boolean} [reportParams.loading] - Loading state ref (cleared on completion)
     * @param {Object} moreParams - Additional parameters merged with reportParams
     * @returns {Promise<void>} Resolves when download is complete
     * @throws {Error} When API request fails (network errors, server errors)
     *
     * @example
     * // Generate sales report
     * await reportsClient.generateReport('sales', { pType: 'daily', date: '2026-02-20' })
     *
     * @example
     * // Generate favorites report with loading state
     * const loading = ref(true)
     * await reportsClient.generateReport('favorites', {}, { loading })
     * // loading.value is now false
     */
    async generateReport(name, reportParams = {}, moreParams = {}) {
      const req_name = normalizeReportName(name, reportParams)

      const payload = buildReportPayload(req_name, {
        ...reportParams,
        ...moreParams,
      })

      try {
        const response = await api.post(
          toSameOriginUrl("/edit/newreports"),
          payload,
          {
            headers: { "Content-Type": "multipart/form-data" },
            responseType: "blob",
          }
        )

        let downloadName = name
        if (downloadName.indexOf("favorites") > -1) {
          downloadName = "favorites"
        }

        await validateXlsxBlob(
          response.data,
          getResponseHeader(response, "content-type")
        )
        downloadFile(response.data, downloadName)
      } catch (error) {
        throw await decodeBlobErrorBody(error)
      } finally {
        if (reportParams.loading !== undefined) {
          reportParams.loading = false
        }
      }
    },

    /**
     * Open one direct Newreports action through authenticated axios transport.
     *
     * @param {string} actionName - Direct Newreports route name (for example report_top_artists or search_stat)
     * @returns {Promise<void>} Resolves when the download/open flow has been delegated to the browser
     */
    async openLinkedReportAction(actionName) {
      const actionConfig = LINKED_REPORT_ACTIONS[actionName]
      if (!actionConfig) {
        throw new Error(`Unknown linked report action: ${actionName}`)
      }

      const reservedTab =
        actionConfig.transport === "open" ? openReservedReportTab() : null

      try {
        const response = await getLinkedReportTransport().get(
          toSameOriginUrl(actionConfig.path),
          createLinkedReportRequestConfig(actionConfig)
        )

        if (actionConfig.transport === "download") {
          await validateXlsxBlob(
            response.data,
            getResponseHeader(response, "content-type")
          )
          downloadLinkedReportBlob(
            response.data,
            resolveLinkedReportFilename(response, actionConfig, actionName)
          )
          return
        }

        await openBlobInNewTab(response.data, reservedTab, {
          baseHref: toSameOriginUrl(actionConfig.path),
        })
      } catch (error) {
        closeReservedReportTab(reservedTab)
        throw error
      }
    },

    /**
     * Check if user has permission for report
     *
     * This is a placeholder method for API completeness.
     * Actual permission checks are delegated to useUserPermissions composable.
     *
     * @param {string} name - Report name
     * @returns {boolean} Always returns true (delegated to composable)
     *
     * @example
     * if (reportsClient.isPermitted('sales')) {
     *   await reportsClient.generateReport('sales', params)
     * }
     */
    // eslint-disable-next-line no-unused-vars
    isPermitted: (_name) => {
      return true
    },
  }
}

// Singleton instance for direct import
export const reportsClient = createReportsClient()
